import { Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type FieldSource = 'OVERRIDE' | 'GENERAL' | 'DEFAULT';

export interface FieldSourceChipProps {
  source: FieldSource;
  /** *(delta 2026-09-23)* `DEFAULT` while an uncommitted "Use platform default" is pending — the
   * dashed "Platform default · not saved yet" variant (design §17.2). Ignored for other sources. */
  pending?: boolean;
}

/**
 * Per-field provenance chip. *(delta 2026-09-23)* No longer channel-tab-only: `DEFAULT` is reachable
 * on the General tab too, now that `defaultedFields[]`/`platformDefaults` make it explicit
 * (design §17.1/§17.2 — supersedes the 2026-09-18 "only on channel tabs" note).
 */
export function FieldSourceChip({ source, pending = false }: FieldSourceChipProps) {
  const { t } = useTranslation('chatbot');

  if (source === 'DEFAULT') {
    return (
      <Tooltip title={t('source.defaultTooltip')}>
        <Chip
          size="small"
          variant="outlined"
          color={pending ? 'primary' : 'default'}
          label={pending ? t('source.defaultPending') : t('source.default')}
          sx={pending ? { height: 20, borderStyle: 'dashed' } : { height: 20 }}
        />
      </Tooltip>
    );
  }
  if (source === 'OVERRIDE') {
    return (
      <Chip
        size="small"
        color="primary"
        variant="filled"
        label={t('source.override')}
        sx={{ height: 20 }}
      />
    );
  }
  return <Chip size="small" variant="outlined" label={t('source.general')} sx={{ height: 20 }} />;
}
